const WATCHED_EVENTS = new Set([
  "focus",
  "focusin",
  "focusout",
  "blur",
  "input",
  "beforeinput",
  "selectionchange",
  "keydown",
  "keypress",
]);

type Listener = EventListenerOrEventListenerObject | null;

const wrappers = new WeakMap<object, WeakMap<object, EventListenerOrEventListenerObject>>();

function getTargetLabel(target: EventTarget) {
  if (target === window) return "window";
  if (target === document) return "document";
  if (target instanceof Element) {
    const id = target.id ? `#${target.id}` : "";
    const tag = target.tagName.toLowerCase();
    return `${tag}${id}`;
  }
  return target.constructor?.name ?? "EventTarget";
}

function getListenerLabel(listener: Listener) {
  if (!listener) return "null-listener";
  if (typeof listener === "function") return listener.name || "anonymous-listener";
  return listener.handleEvent?.name || listener.constructor?.name || "listener-object";
}

function rememberWrapper(target: EventTarget, listener: Listener, wrapped: EventListenerOrEventListenerObject) {
  if (!listener || (typeof listener !== "function" && typeof listener !== "object")) return;
  let byListener = wrappers.get(target);
  if (!byListener) {
    byListener = new WeakMap<object, EventListenerOrEventListenerObject>();
    wrappers.set(target, byListener);
  }
  byListener.set(listener as object, wrapped);
}

function getWrapper(target: EventTarget, listener: Listener) {
  if (!listener || (typeof listener !== "function" && typeof listener !== "object")) return listener;
  return wrappers.get(target)?.get(listener as object) ?? listener;
}

export function installStartupDiagnostics() {
  if (typeof window === "undefined") return;
  const marker = "__gamoStartupDiagnosticsInstalled";
  const globalWindow = window as unknown as Window & Record<string, unknown>;
  if (globalWindow[marker]) return;
  globalWindow[marker] = true;

  const originalAdd = EventTarget.prototype.addEventListener;
  const originalRemove = EventTarget.prototype.removeEventListener;

  console.info("[GAMO_DIAG] startup diagnostics installed before React render", {
    href: window.location.href,
    userAgent: navigator.userAgent,
  });

  EventTarget.prototype.addEventListener = function patchedAddEventListener(
    type: string,
    listener: Listener,
    options?: boolean | AddEventListenerOptions,
  ) {
    // V19: BLOCK selectionchange registration on document to confirm
    // whether the freeze disappears when React's SelectionEventPlugin
    // listener never runs.
    if (type === "selectionchange" && this === document) {
      console.warn("[GAMO_DIAG] BLOCKED selectionchange registration on document", {
        listener: getListenerLabel(listener),
        stack: new Error("blocked selectionchange").stack,
      });
      return;
    }

    if (!WATCHED_EVENTS.has(type) || !listener) {
      return originalAdd.call(this, type, listener, options);
    }

    const target = this;
    const targetLabel = getTargetLabel(target);
    const listenerLabel = getListenerLabel(listener);
    const registrationStack = new Error(`[GAMO_DIAG] registered ${type}`).stack;

    const wrapped: EventListener = function gamoDiagnosticEventWrapper(this: EventTarget, event: Event) {
      const startedAt = performance.now();
      console.info(`[GAMO_DIAG] EVENT START ${type}`, {
        target: targetLabel,
        listener: listenerLabel,
        eventTarget: getTargetLabel(event.target as EventTarget),
        activeElement: document.activeElement ? getTargetLabel(document.activeElement) : null,
        registrationStack,
      });

      try {
        if (typeof listener === "function") {
          return listener.call(this, event);
        }
        return listener.handleEvent(event);
      } finally {
        console.info(`[GAMO_DIAG] EVENT END ${type}`, {
          target: targetLabel,
          listener: listenerLabel,
          durationMs: Number((performance.now() - startedAt).toFixed(2)),
          activeElement: document.activeElement ? getTargetLabel(document.activeElement) : null,
        });
      }
    };

    rememberWrapper(target, listener, wrapped);
    console.info(`[GAMO_DIAG] addEventListener ${type}`, {
      target: targetLabel,
      listener: listenerLabel,
      stack: registrationStack,
    });
    return originalAdd.call(target, type, wrapped, options);
  };

  EventTarget.prototype.removeEventListener = function patchedRemoveEventListener(
    type: string,
    listener: Listener,
    options?: boolean | EventListenerOptions,
  ) {
    return originalRemove.call(this, type, getWrapper(this, listener), options);
  };
}